import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Trash2, ChevronDown, Filter as FilterIcon } from "lucide-react";
import { PARAM_TYPES, PARAM_OPS, paramType } from "@/lib/paramEngine";
import api from "@/lib/api";
import { smartSortValues } from "@/lib/filterValues";

// Item 30: live control for one parameter (non-numeric types; numbers keep the slider).
export function ParamControl({ def, value, onChange }) {
  const t = paramType(def);
  const [opts, setOpts] = useState(Array.isArray(def.values) ? def.values : []);

  useEffect(() => {
    if (t !== "list" || !def.dataset_id || !def.field || (def.values || []).length) return;
    let live = true;
    api.post(`/datasets/${def.dataset_id}/distinct`, { field: def.field, filters: [], limit: 500 })
      .then(({ data }) => {
        if (!live) return;
        const vals = (data?.values || data || []).map((v) => (v && typeof v === "object" ? v.value : v)).filter((v) => v !== null && v !== "");
        setOpts(smartSortValues(vals.map(String), "asc"));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [t, def.dataset_id, def.field, def.values]);

  if (t === "boolean") {
    return <Switch data-testid={`param-bool-${def.name}`} checked={!!value} onCheckedChange={onChange} />;
  }
  if (t === "date") {
    return <Input data-testid={`param-date-${def.name}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="h-7 w-[140px] text-xs bg-[color:var(--surface)]" />;
  }
  if (t === "list" && def.multi) {
    const sel = Array.isArray(value) ? value : (value ? [value] : []);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`param-multi-${def.name}`} className="h-7 min-w-[140px] px-2 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] text-xs text-[color:var(--ink-2)] flex items-center gap-1">
            <span className="truncate flex-1 text-left">{sel.length ? `${sel.length} selected` : "All"}</span>
            <ChevronDown className="w-3 h-3 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[300px] overflow-y-auto">
          {opts.map((o) => (
            <DropdownMenuCheckboxItem key={o} checked={sel.includes(o)}
              onCheckedChange={(c) => onChange(c ? [...sel, o] : sel.filter((x) => x !== o))}>{o}</DropdownMenuCheckboxItem>
          ))}
          {!opts.length && <div className="px-2 py-1 text-[11px] text-[color:var(--ink-3)]">No values</div>}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  if (t === "list") {
    return (
      <Select value={value ? String(value) : "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger data-testid={`param-list-${def.name}`} className="h-7 w-[150px] text-xs bg-[color:var(--surface)]"><SelectValue placeholder="All" /></SelectTrigger>
        <SelectContent className="max-h-[300px]">
          <SelectItem value="__all__">All</SelectItem>
          {opts.map((o) => <SelectItem key={o} value={String(o)}>{String(o)}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  return <Input data-testid={`param-text-${def.name}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
    placeholder={def.placeholder || "value"} className="h-7 w-[150px] text-xs bg-[color:var(--surface)]" />;
}

// One row of the parameter editor: identity + type-specific settings + optional filter binding.
export function ParamDefRow({ d, index, datasets = [], update, remove }) {
  const t = paramType(d);
  const [cols, setCols] = useState([]);
  useEffect(() => {
    if (!d.dataset_id) { setCols([]); return; }
    let live = true;
    api.get(`/datasets/${d.dataset_id}`).then(({ data }) => {
      if (live) setCols((data?.columns || []).map((c) => c.name || c));
    }).catch(() => {});
    return () => { live = false; };
  }, [d.dataset_id]);

  return (
    <div data-testid="whatif-def-row" className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2 space-y-1.5">
      <div className="flex gap-1.5 items-end">
        <div className="flex-1 min-w-0"><Label className="text-[10px]">Name</Label>
          <Input data-testid="whatif-def-name" className="mt-1 h-8 text-xs bg-[color:var(--surface)]" value={d.name} onChange={(e) => update({ name: e.target.value })} placeholder="growth_rate" /></div>
        <div className="flex-1 min-w-0"><Label className="text-[10px]">Label</Label>
          <Input className="mt-1 h-8 text-xs bg-[color:var(--surface)]" value={d.label || ""} onChange={(e) => update({ label: e.target.value })} placeholder="Growth rate" /></div>
        <div className="w-[136px] shrink-0"><Label className="text-[10px]">Type</Label>
          <Select value={t} onValueChange={(v) => update({ type: v, default: v === "number" ? 0 : v === "boolean" ? false : "" })}>
            <SelectTrigger data-testid={`param-type-${index}`} className="mt-1 h-8 text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
            <SelectContent>{PARAM_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-[color:var(--danger)]" onClick={remove}><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>

      {t === "number" && (
        <div className="grid grid-cols-4 gap-1.5">
          <div><Label className="text-[10px]">Min</Label><Input type="number" className="mt-1 h-8 text-xs bg-[color:var(--surface)]" value={d.min ?? 0} onChange={(e) => update({ min: e.target.value })} /></div>
          <div><Label className="text-[10px]">Max</Label><Input type="number" className="mt-1 h-8 text-xs bg-[color:var(--surface)]" value={d.max ?? 1} onChange={(e) => update({ max: e.target.value })} /></div>
          <div><Label className="text-[10px]">Step</Label><Input type="number" className="mt-1 h-8 text-xs bg-[color:var(--surface)]" value={d.step ?? 0.05} onChange={(e) => update({ step: e.target.value })} /></div>
          <div><Label className="text-[10px]">Default</Label><Input type="number" className="mt-1 h-8 text-xs bg-[color:var(--surface)]" value={d.default ?? 0} onChange={(e) => update({ default: e.target.value })} /></div>
        </div>
      )}
      {t === "list" && (
        <div className="flex gap-1.5 items-end">
          <div className="flex-1 min-w-0"><Label className="text-[10px]">Values (comma separated — leave empty to read them from the bound field)</Label>
            <Input data-testid={`param-values-${index}`} className="mt-1 h-8 text-xs bg-[color:var(--surface)]"
              value={Array.isArray(d.values) ? d.values.join(", ") : (d.values || "")}
              onChange={(e) => update({ values: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) })} placeholder="North, South, East" /></div>
          <div className="shrink-0 pb-1 flex items-center gap-1.5">
            <Label className="text-[10px]">Multi</Label>
            <Switch data-testid={`param-multi-toggle-${index}`} checked={!!d.multi} onCheckedChange={(v) => update({ multi: v, default: v ? [] : "" })} />
          </div>
        </div>
      )}
      {(t === "text" || t === "date") && (
        <div><Label className="text-[10px]">Default</Label>
          <Input data-testid={`param-default-${index}`} type={t === "date" ? "date" : "text"} className="mt-1 h-8 text-xs bg-[color:var(--surface)]" value={d.default || ""} onChange={(e) => update({ default: e.target.value })} /></div>
      )}
      {t === "boolean" && (
        <div className="flex items-center gap-2"><Label className="text-[10px]">Default on</Label>
          <Switch data-testid={`param-default-${index}`} checked={!!d.default} onCheckedChange={(v) => update({ default: v })} /></div>
      )}

      <div className="pt-1 border-t border-[color:var(--glass-border)]">
        <Label className="text-[10px] text-[color:var(--ink-3)] flex items-center gap-1"><FilterIcon className="w-3 h-3" />Also filter data with this parameter (optional)</Label>
        <div className="flex gap-1.5 mt-1">
          <Select value={d.dataset_id || "__none__"} onValueChange={(v) => update({ dataset_id: v === "__none__" ? null : v, field: null })}>
            <SelectTrigger data-testid={`param-dataset-${index}`} className="h-8 text-xs bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue placeholder="No dataset" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No dataset (parameter only)</SelectItem>
              {datasets.map((ds) => <SelectItem key={ds.id} value={ds.id}>{ds.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={d.field || "__none__"} onValueChange={(v) => update({ field: v === "__none__" ? null : v, op: d.op || (d.multi ? "in" : "eq") })} disabled={!d.dataset_id}>
            <SelectTrigger data-testid={`param-field-${index}`} className="h-8 text-xs bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue placeholder="No field" /></SelectTrigger>
            <SelectContent className="max-h-[260px]">
              <SelectItem value="__none__">No field</SelectItem>
              {cols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={d.op || "eq"} onValueChange={(v) => update({ op: v })} disabled={!d.field}>
            <SelectTrigger data-testid={`param-op-${index}`} className="h-8 text-xs bg-[color:var(--surface)] w-[110px] shrink-0"><SelectValue /></SelectTrigger>
            <SelectContent>{PARAM_OPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
